/*
 * ProcessInviteGuest Component
 *
 * This component manages the process of inviting guests to an event, supporting both single and bulk (multi-invite) flows.
 *
 * Features:
 * - Allows selection of invitation type (single or multiple).
 * - Collects guest information and invitation details via a form.
 * - Supports bulk invite by parsing multi-line text input for multiple guests.
 * - Integrates with shared bulk process utilities for parsing, submission, and navigation.
 * - Displays success and summary modals upon completion.
 *
 * This component is part of the unified bulk process system, sharing navigation and utility logic with other processes (box office, import CSV, offline sales).
 */

import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { STEP_SELECT_TYPE, STEP_SUCCESS } from '@constants/process-steps';
import { useGetOfferAddon } from '@hooks/addons/use-get-offer-addons';
import { useImportMultiInvite } from '@hooks/import/use-import-multi-invite';
import { useAddGuest } from '@hooks/use-add-guest/use-add-guest';
import { useBulkProcessNavigation } from '@hooks/use-bulk-process-navigation';
import { useProcessForm } from '@hooks/use-process-form/use-process-form';
import { UseProcessFormReturn } from '@hooks/use-process-form/use-process-form.types';
import { IMPORT_TYPE_INVITATION } from '@pages/events/utils/import-process-utils';
import { handleBulkProcessSubmission } from '@utils/bulk-process/bulk-process-utils';

import { createFieldsFromBookingFormFields } from '../utils/invite-guest-utils';

import { InviteGuestSteps } from './invite-guest-steps';
import {
  InviteGuestForm,
  MULTIPLE_INVITE,
  OPTION_FILL_INFO,
  ProcessFormFields,
  ProcessInviteGuestStep,
  SINGLE_INVITE,
} from './process-invite-guest.type';

export const ProcessInviteGuest = () => {
  // Use the extended useProcessForm hook with invite guest specific configuration and proper typing
  const processForm: UseProcessFormReturn = useProcessForm({
    includeInvitationFields: true,
    includeGuestInfo: true,
    additionalDefaults: {
      type: SINGLE_INVITE,
      infoType: OPTION_FILL_INFO,
    },
  });

  const {
    methods,
    invitationId,
    type: invitationType,
    email,
    eventId,
    quantity,
    step,
    setStep,
    closeModal,
    offer,
  } = processForm;

  const { handleSubmit } = methods;

  // Data fetching
  const { data: addons } = useGetOfferAddon({
    offerId: invitationId,
    enabled: offer && offer?.addOns?.length > 0,
  });
  const availableAddons = addons?.filter((addon) => {
    if (!addon.quantity) return true;

    return addon.soldItems < addon.quantity;
  });

  const {
    data: addGuestData,
    mutate: addGuest,
    isSuccess: addGuestSuccess,
    isPending: isAddingGuest,
  } = useAddGuest({ hideToast: true });
  const {
    data: importMultiInviteData,
    mutate: importMultiInvite,
    isSuccess: importSuccess,
    isPending: isImporting,
  } = useImportMultiInvite({ hideToast: true });

  // Use the shared import navigation hook
  useBulkProcessNavigation({
    eventId,
    importType: IMPORT_TYPE_INVITATION,
    importSuccess,
    importData: importMultiInviteData,
  });

  const newSendInvitationProcess = () => setStep(STEP_SELECT_TYPE);

  const handleSingleInviteSubmit = (e: ProcessFormFields) => {
    addGuest({
      formValue: {
        offerId: e.invitationId ?? '',
        bookingFormFields: createFieldsFromBookingFormFields(e as InviteGuestForm),
        ticketHolderFormFields: [],
        numTickets: e.quantity,
        channelId: e.channelId,
      },
    });
  };

  const handleMultiInviteSubmit = (e: ProcessFormFields) => {
    if (e.multiInviteText) {
      handleBulkProcessSubmission(
        e.multiInviteText,
        {
          offerId: e.invitationId ?? '',
          channelId: e.channelId,
          eventId: e.eventId,
        },
        importMultiInvite
      );
    }
  };

  const onSubmit = (e: ProcessFormFields) => {
    if (invitationType === SINGLE_INVITE) {
      handleSingleInviteSubmit(e);
    } else if (invitationType === MULTIPLE_INVITE) {
      handleMultiInviteSubmit(e);
    }
  };

  useEffect(() => {
    if (addGuestSuccess) {
      setStep(STEP_SUCCESS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addGuestSuccess]);

  // Navigation is now handled automatically by useBulkProcessNavigation hook

  return (
    <form className='flex justify-end flex-col relative'>
      <FormProvider {...methods}>
        <InviteGuestSteps
          step={step as ProcessInviteGuestStep}
          setStep={setStep}
          offer={offer}
          availableAddons={availableAddons ?? []}
          addGuestData={addGuestData}
          email={email}
          quantity={quantity}
          isSubmitting={isAddingGuest || isImporting}
          onSubmit={handleSubmit(onSubmit)}
          onClose={closeModal}
          newSendInvitationProcess={newSendInvitationProcess}
        />
      </FormProvider>
    </form>
  );
};
